import { Button, Card, Flex, SimpleTable, Typography } from "djuno-design";
import { useState } from "react";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const ButtonPage = () => {
  const [btnLoading, setBtnLoading] = useState(false);
  const handleClick = () => {
    setBtnLoading(true);
    setTimeout(() => setBtnLoading(false), 3000);
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Button</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Button component in the Djuno Design system is a flexible and
            customizable UI element designed to meet the needs of various user
            interfaces. Built with performance and accessibility in mind, this
            component allows for seamless integration into your applications
            while maintaining a consistent design language. With support for
            different visual variants, sizes, and states (such as loading), the
            Button component can easily adapt to the specific needs of your
            project.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Button component provides several styling options via predefined
            variants that control its visual appearance. It supports four main
            variants:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Simple
                </Text>
                : A transparent background with a minimal border and subtle
                color shifts, ideal for secondary or less prominent actions.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Primary
                </Text>
                : A solid, bold style, used for key actions in the UI.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Light
                </Text>
                : A softer, lighter variant for a more subtle appearance while
                maintaining a clear interactive state.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Danger
                </Text>
                : A red-colored variant, often used for destructive actions like
                deletions or irreversible tasks.
              </Text>
            </li>
          </ul>
          <Text size="sm" className="mt-4">
            In addition, the Button component offers three different sizes{" "}
            <Text size="sm" strong>
              small
            </Text>
            ,{" "}
            <Text size="sm" strong>
              medium
            </Text>
            , and{" "}
            <Text size="sm" strong>
              large
            </Text>{" "}
            allowing you to pick the appropriate dimensions based on the context
            of use. These variants can be mixed and matched to achieve the
            desired effect.
          </Text>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <Text size="sm">
            The Button component also supports advanced features like loading
            states, which are useful when the button action triggers background
            tasks or processing. A built-in Tooltip component enhances the user
            experience by providing additional context or information when
            hovering over the button.
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4">
            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="uiType"
                codeViewerProps={{
                  code: ButtonExample1,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-2">
                  <Button>Default</Button>
                  <Button
                    uiType="primary"
                    loading={btnLoading}
                    loadingType="elastic"
                    onClick={handleClick}
                  >
                    Primary
                  </Button>
                  <Button uiType="light">Light</Button>
                  <Button uiType="danger">Danger</Button>
                  <Button uiType="dangerLight">Danger Light</Button>
                </Flex>
              </ExpandableCard>
              <ExpandableCard
                title="disabled"
                codeViewerProps={{
                  code: ButtonExample2,
                }}
              >
                <Flex items="center" className="gap-2">
                  <Button disabled>Default</Button>
                  <Button
                    uiType="primary"
                    loading={btnLoading}
                    onClick={handleClick}
                    disabled
                  >
                    Primary
                  </Button>
                  <Button uiType="light" disabled>
                    Light
                  </Button>
                  <Button uiType="danger" disabled>
                    Danger
                  </Button>
                  <Button uiType="dangerLight" disabled>
                    Danger Light
                  </Button>
                </Flex>
              </ExpandableCard>
            </Flex>

            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="loading"
                codeViewerProps={{
                  code: ButtonExample3,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-2">
                  <Button loading loadingType="simple">
                    simple
                  </Button>
                  <Button loading loadingType="cutoff">
                    cutoff
                  </Button>
                  <Button loading loadingType="elastic">
                    elastic
                  </Button>
                </Flex>
              </ExpandableCard>

              <ExpandableCard
                title="tooltip"
                codeViewerProps={{
                  code: ButtonExample4,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-2">
                  <Button
                    tooltip={{
                      content: "this is a test tooltip",
                      theme: "primary",
                    }}
                  >
                    primary
                  </Button>
                  <Button
                    tooltip={{
                      content: "this is a test tooltip",
                      theme: "error",
                    }}
                  >
                    error
                  </Button>
                  <Button
                    tooltip={{
                      content: "this is a test tooltip",
                      theme: "primary",
                      clickable: true,
                    }}
                  >
                    clickable
                  </Button>
                </Flex>
              </ExpandableCard>
            </Flex>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* uiType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Type of UI for the button</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Button.ts#L34"}
                        size="xs"
                        code
                      >
                        ButtonTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Size of the button</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Tooltip configuration for the button
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Indicates if the button is in a loading state
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Type of loading indicator: 'simple' or custom
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* buttonProps */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">"button props"</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        HTML properties for the button element.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`React.ButtonHTMLAttributes<HTMLButtonElement>`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const ButtonExample1 = `
  <Button>Default</Button>
  <Button
    uiType="primary"
    loading={btnLoading}
    loadingType="elastic"
    onClick={handleClick}
  >
    Primary
  </Button>
  <Button uiType="light">Light</Button>
  <Button uiType="danger">Danger</Button>
  <Button uiType="dangerLight">Danger Light</Button>
`;

const ButtonExample2 = `
  <Button disabled>Default</Button>
  <Button
    uiType="primary"
    loading={btnLoading}
    onClick={handleClick}
    disabled
  >
    Primary
  </Button>
  <Button uiType="light" disabled>
    Light
  </Button>
  <Button uiType="danger" disabled>
    Danger
  </Button>
  <Button uiType="dangerLight" disabled>
    Danger Light
  </Button>
`;

const ButtonExample3 = `
  <Button loading loadingType="simple">
    simple
  </Button>
  <Button loading loadingType="cutoff">
    cutoff
  </Button>
  <Button loading loadingType="elastic">
    elastic
  </Button>
`;
const ButtonExample4 = `
  <Button
    tooltip={{
      content: "this is a test tooltip",
      theme: "primary",
    }}
  >
    primary
  </Button>
  <Button
    tooltip={{
      content: "this is a test tooltip",
      theme: "error",
    }}
  >
    error
  </Button>
  <Button
    tooltip={{
      content: "this is a test tooltip",
      theme: "primary",
      clickable: true,
    }}
  >
    clickable
  </Button>
`;

export default ButtonPage;
